import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "./motion";
import { CLIENT_SERVICES } from "./content";

const PARTNER_POINTS = [
  "Health & Safety systems and documentation",
  "SSIP, CHAS, SMAS or SafeContractor pathways",
  "Financial resilience to deliver larger contracts",
  "Professional processes, references and reporting",
];

export default function TwoSides() {
  return (
    <section className="vw-section bg-[var(--vw-paper)]" data-testid="two-sides-section">
      <div className="vw-container">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div className="max-w-3xl">
              <div className="vw-overline mb-4">Two Sides. One Supply Chain.</div>
              <h2 className="font-display text-4xl lg:text-6xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.02]">
                We work both sides of the construction supply chain.
              </h2>
            </div>
            <div className="max-w-md text-[var(--vw-muted)] text-base leading-relaxed">
              Verified Workforce exists to make UK construction supply chains broader,
              stronger and more resilient. We help major clients find the right partners —
              and help ambitious SMEs become ready to work with them.
            </div>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* CLIENTS */}
          <Reveal>
            <div className="relative overflow-hidden h-full bg-[var(--vw-navy)] text-white p-10 lg:p-14 border border-white/5 group">
              <div
                className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-700 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1400&q=70)",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(140deg, rgba(10,15,36,0.92), rgba(10,15,36,0.75))",
                }}
              />
              <div className="relative">
                <div className="vw-overline vw-overline--gold mb-5">
                  For Principal Contractors &amp; Clients
                </div>
                <h3 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                  Strengthen Your Supply Chain
                </h3>
                <p className="mt-6 text-white/75 leading-relaxed max-w-xl">
                  Access a growing network of specialist construction businesses across
                  planned maintenance, refurbishment and specialist trade packages.
                </p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[14px] text-white/80">
                  {CLIENT_SERVICES.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[var(--vw-gold)] mt-0.5 flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/for-clients"
                  className="vw-btn vw-btn--gold mt-10"
                  data-testid="two-sides-clients-cta"
                >
                  Strengthen Your Supply Chain
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>

          {/* PARTNERS */}
          <Reveal delay={0.1}>
            <div className="relative h-full bg-white p-10 lg:p-14 border border-[var(--vw-line)]">
              <div className="vw-overline mb-5">For Contractors &amp; SMEs</div>
              <h3 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] text-[var(--vw-navy)]">
                Get Ready for Bigger Contracts
              </h3>
              <p className="mt-6 text-[var(--vw-muted)] leading-relaxed max-w-xl">
                Being good at construction isn&apos;t always enough to access major
                contracts. Larger organisations expect contractors to demonstrate strong
                Health &amp; Safety systems, compliance, financial resilience and
                professional processes.
              </p>
              <p className="mt-4 text-[var(--vw-muted)] leading-relaxed max-w-xl">
                Verified Workforce helps capable contractors bridge that gap — so you can
                work with Tier 1 and Tier 2 contractors, developers, councils and housing
                associations.
              </p>
              <ul className="mt-8 space-y-2 text-[14px] text-[var(--vw-charcoal)]">
                {PARTNER_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-[var(--vw-blue)] mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/delivery-partners#apply"
                  className="vw-btn vw-btn--primary"
                  data-testid="two-sides-partners-cta"
                >
                  Become a Verified Partner
                  <ArrowUpRight size={16} />
                </Link>
                <Link to="/readiness" className="vw-btn vw-btn--outline-dark" data-testid="two-sides-readiness-cta">
                  Check Your Contract Readiness
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
